import * as THREE from 'three';
import type { WorldSnapshot } from '../../../shared/network/protocol.js';
import type { CombatVisualEvent } from '../../../shared/game/combat-types.js';
import { ownFigureMaterials, shareFigureMaterials } from '../entities/figure-materials.js';
import { tintPaletteSkin } from '../entities/character-palettes.js';
import { disposeEffect, effectMaterial, effectParticles, shadeEffect, spiralRibbon } from './effect-surfaces.js';
import { distortionShell } from './howl-wave.js';
import type { SpellLight } from './spell-effects.js';
import { RAGE_PULSE, rageBeat } from './rage-pulse.js';

const TAU = Math.PI * 2;
/** The Rage's colours: the warmth under the skin, and the embers and fire of its burst. */
export const RAGE = { heat: 0xc8401c, ember: 0xe0743a, flame: 0xb8461f,
  /** How warm skin with its own material glows under the surface. */
  glow: .2,
  /** Seconds the Rage takes to come up and to cool away, and the God's Strength burst's length. */
  rise: .5, cool: .9, burst: 1.3 } as const;
/** Ember streams rising off a Raging body: two always, and one more for each Fury Die held. Each runs its own cycle, staggered, so embers never stop rising. */
const EMBER_STREAMS = 5;
/** The air above a Raging body: heat rising off it in slow, wavering columns. */
const HEAT = 'sin(vWarp.y * 7.0 - uTime * 5.5 + sin(atan(vWarp.z, vWarp.x) * 3.0 + uTime * 1.3) * 1.6) * sin(atan(vWarp.z, vWarp.x) * 5.0 + vWarp.y * 2.5 - uTime * 2.0)';
const additive = { transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false } as const;

/** A flat disc of light on the ground: a soft pool (`uPool`) with a hotter ring near its rim that `uRing` (0–1) brings up. */
function groundGlow(color: number) {
  return new THREE.ShaderMaterial({ ...additive, side: THREE.DoubleSide,
    uniforms: { uColor: { value: new THREE.Color(color) }, uOpacity: { value: 0 }, uRing: { value: 0 }, uPool: { value: 1 } },
    vertexShader: `varying float vR; void main() { vR = length(position.xy); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `uniform vec3 uColor; uniform float uOpacity; uniform float uRing; uniform float uPool; varying float vR;
      void main() {
        float pool = pow(max(0.0, 1.0 - vR), 2.4) * .55 * uPool, ring = exp(-pow((vR - .82) * 9.0, 2.0)) * (1.0 - smoothstep(.96, 1.0, vR)) * uRing;
        gl_FragColor = vec4(uColor * (1.0 + ring), (pool + ring * .9) * uOpacity);
      }` });
}

interface Surface { material: THREE.MeshStandardMaterial; color: THREE.Color; emissive: THREE.Color }
/** A Raging hero: its own copies of its materials, the skin flushed; the heat over it and a few embers. */
interface Raging {
  group: THREE.Object3D; surfaces: Surface[];
  /** Palette draws whose folded skin takes `tint` (`tintPaletteSkin`). */
  palettes: THREE.Mesh[]; tint: THREE.Vector3;
  aura: THREE.Group; heat: ReturnType<typeof distortionShell>; embers: THREE.Points[];
  /** How far into the Rage the look is (0–1), how many Fury Dice it holds (eased), and a seed that keeps two Berserkers out of step. */
  level: number; fury: number; seed: number; raging: boolean;
}
/** God's Strength: the moment a Rage goes off. */
interface Burst { heroId: string; at: number; group: THREE.Group; wave: ReturnType<typeof distortionShell>; pillar: THREE.Group; ring: THREE.Mesh<THREE.CircleGeometry, THREE.ShaderMaterial>; sparks: THREE.Points }

/**
 * The Berserker's Rage, presentation only. While the server says a hero Rages its bare skin flushes blood red, throbbing with a
 * heartbeat that quickens as its Fury Dice fill; its clothes and gear keep their colours. Heat bends the air above it and a few embers
 * rise. Each Rage goes off like Sven's God's Strength, kept to the hero's size: a shock wave bends the ground round it, a short column
 * of fire coils up and burns away, and sparks fly. That all you got?! deepens the flush for a moment as the dice are thrown against a blow.
 */
export class BerserkerEffects {
  private raging = new Map<string, Raging>();
  private bursts: Burst[] = [];
  /** When each hero last threw Fury Dice against a blow, or Raged: the flush deepens and fades back. */
  private flares = new Map<string, { at: number; power: number }>();
  private lastTime: number | undefined;
  readonly lights: SpellLight[] = [];
  get visualObjects(): THREE.Object3D[] { return [...[...this.raging.values()].map(state => state.aura), ...this.bursts.map(burst => burst.group)]; }
  constructor(private scene: THREE.Scene, private heights: ReadonlyMap<string, number>) {}

  receive(event: CombatVisualEvent, at: number) {
    if (event.kind !== 'ability') return;
    if (event.abilityId === 'rage') {
      this.flares.set(event.actorId, { at, power: 1 });
      this.bursts.push(this.burst(event.actorId, at));
    } else if (event.abilityId === 'that-all-you-got') this.flares.set(event.actorId, { at, power: .7 });
  }

  update(snapshot: WorldSnapshot, earlier: WorldSnapshot, alpha: number, positions: ReadonlyMap<string, THREE.Object3D>, ppm = 40) {
    const time = earlier.gameTime + (snapshot.gameTime - earlier.gameTime) * alpha;
    const dt = THREE.MathUtils.clamp(time - (this.lastTime ?? time), 0, .1); this.lastTime = time;
    this.lights.length = 0;
    const heroes = new Map(snapshot.heroes.map(hero => [hero.id, hero]));
    for (const hero of snapshot.heroes) {
      const fury = hero.class.fury, figure = positions.get(hero.id);
      if (!fury?.raging || hero.health.dead || !figure) continue;
      const state = this.raging.get(hero.id);
      if (!state) this.raging.set(hero.id, this.ignite(figure, hero.id, hero.radius));
      else if (state.group !== figure) { this.extinguish(hero.id); this.raging.set(hero.id, this.ignite(figure, hero.id, hero.radius)); }
    }
    for (const [id, state] of this.raging) {
      const hero = heroes.get(id), figure = positions.get(id);
      if (!hero || !figure || figure !== state.group || hero.health.dead) { this.extinguish(id); continue; }
      state.raging = !!hero.class.fury?.raging;
      state.level = THREE.MathUtils.clamp(state.level + (state.raging ? dt / RAGE.rise : -dt / RAGE.cool), 0, 1);
      if (state.level <= 0 && !state.raging) { this.extinguish(id); continue; }
      state.fury += ((hero.class.fury?.dice.length ?? 0) - state.fury) * Math.min(1, dt * 3);
      this.burn(state, figure, id, time, ppm);
    }
    this.bursts = this.bursts.filter(burst => this.blast(burst, positions, time, ppm));
  }

  /** Only bare skin flushes: skin folded into palette draws, and any `skin` material of its own; clothes, hair and gear keep their colours. */
  private ignite(figure: THREE.Object3D, id: string, radius: number): Raging {
    ownFigureMaterials(figure);
    const surfaces: Surface[] = [], palettes: THREE.Mesh[] = [], tint = new THREE.Vector3(1, 1, 1), seen = new Set<THREE.Material>();
    figure.traverse(node => {
      if (!(node instanceof THREE.Mesh)) return;
      if (!Array.isArray(node.material) && node.material.name === 'character-palette') { tintPaletteSkin(node, tint); palettes.push(node); }
      for (const material of [node.material as THREE.Material | THREE.Material[]].flat()) {
        if (seen.has(material) || !(material instanceof THREE.MeshStandardMaterial) || material.name !== 'skin') continue;
        seen.add(material); surfaces.push({ material, color: material.color.clone(), emissive: material.emissive.clone() });
      }
    });
    const height = this.heights.get(id) ?? 2, aura = new THREE.Group(); aura.name = 'berserker-rage';
    const heat = distortionShell('rage-heat', HEAT, 0xffe4d4, 2.6);
    heat.mesh.scale.set(radius * 1.2, height * .62, radius * 1.2); heat.mesh.position.y = height * .66;
    const embers = Array.from({ length: EMBER_STREAMS }, (_, k) => {
      const stream = effectParticles(RAGE.ember, 10, radius * (.55 + k * .08), height * (1.05 + k * .1)); stream.name = 'rage-embers'; stream.rotation.y = k * 1.7; return stream;
    });
    aura.add(heat.mesh, ...embers); this.scene.add(aura);
    return { group: figure, surfaces, palettes, tint, aura, heat, embers, level: 0, fury: 0, seed: RAGE_PULSE.seed(id), raging: true };
  }
  private extinguish(id: string) {
    const state = this.raging.get(id); if (!state) return;
    for (const { material, color, emissive } of state.surfaces) { material.color.copy(color); material.emissive.copy(emissive); }
    for (const mesh of state.palettes) tintPaletteSkin(mesh, null);
    shareFigureMaterials(state.group); disposeEffect(state.aura); state.aura.removeFromParent(); this.raging.delete(id);
  }
  /** The steady burn, stronger with each Fury Die held: a heartbeat in the flush that quickens, more heat and more embers; a deeper flush after each Rage or blow soaked. */
  private burn(state: Raging, figure: THREE.Object3D, id: string, time: number, ppm: number) {
    const level = THREE.MathUtils.smoothstep(state.level, 0, 1);
    const dice = state.fury, beat = rageBeat(time, dice, id);
    // Two thumps a beat, the second softer, like a pulse in the ears.
    const pulse = Math.exp(-(((beat - RAGE_PULSE.thump) * 18) ** 2)) + Math.exp(-(((beat - .24) * 18) ** 2)) * .6;
    const mark = this.flares.get(id), since = mark ? time - mark.at : Infinity, flare = since >= 0 ? Math.exp(-since * 2.4) * (mark?.power ?? 0) : 0;
    const warmth = new THREE.Color(RAGE.heat);
    // Blood under the skin: red held, green and blue drained, so the skin keeps its own tone and shading and only reddens.
    const flush = Math.min(.75, level * (.22 + dice * .14) * (.9 + pulse * .12) + flare * .15);
    const tint = new THREE.Color(1 + flush * .1, 1 - flush * .72, 1 - flush * .74);
    for (const surface of state.surfaces) {
      surface.material.color.copy(surface.color).multiply(tint);
      surface.material.emissive.copy(surface.emissive).lerp(warmth, Math.min(.3, level * RAGE.glow * (.7 + pulse * .4) + flare * .06));
    }
    state.tint.set(tint.r, tint.g, tint.b);
    state.aura.position.copy(figure.position);
    const { uniforms, mesh } = state.heat, waver = .8 + Math.sin(time * 3.1 + state.seed) * .12 + Math.sin(time * 7.7) * .08;
    mesh.visible = level > .01; uniforms.uTime.value = time; uniforms.uRipple.value = level * waver * .8;
    uniforms.uIor.value = 1 + level * (.015 + dice * .012); uniforms.uThickness.value = level * (.1 + dice * .08);
    state.embers.forEach((stream, k) => {
      const shown = THREE.MathUtils.clamp(2 + dice - k, 0, 1);
      stream.visible = shown > .01 && level > .01;
      // Drawn a little larger than spell motes, so single sparks read at game zoom.
      shadeEffect(stream, time, level * shown * (1.1 + dice * .2), (time * .45 + state.seed + k / EMBER_STREAMS) % 1, ppm * 1.6);
      stream.rotation.y = time * .4 + k * 1.7;
    });
  }

  /** God's Strength at the hero's size: a shock wave, a short column of fire and a few sparks, played over `RAGE.burst`. */
  private burst(heroId: string, at: number): Burst {
    const group = new THREE.Group(); group.name = 'gods-strength';
    const wave = distortionShell('rage-wave'); wave.mesh.name = 'rage-wave'; group.add(wave.mesh);
    const pillar = new THREE.Group(); pillar.name = 'rage-pillar'; group.add(pillar);
    // Fire licking upward round the body, and coils of flame wound round it.
    const sheath = new THREE.Mesh(new THREE.CylinderGeometry(.8, 1, 1, 32, 1, true).translate(0, .5, 0), effectMaterial(RAGE.flame, RAGE.ember, 1, .03));
    sheath.userData.baseOpacity = 1.1; pillar.add(sheath);
    for (let i = 0; i < 3; i++) {
      const strand = new THREE.Mesh(spiralRibbon(1.05, 1, .7 + i * .1, .16, i * TAU / 3), effectMaterial(i % 2 ? RAGE.flame : RAGE.heat, RAGE.ember, 4));
      strand.userData.baseOpacity = i % 2 ? .3 : .45; pillar.add(strand);
    }
    const ring = new THREE.Mesh(new THREE.CircleGeometry(1, 56), groundGlow(RAGE.heat)); ring.rotation.x = -Math.PI / 2; ring.position.y = .05; group.add(ring);
    ring.material.uniforms.uPool.value = 0;
    const sparks = effectParticles(RAGE.ember, 16, 1, 1, 1); sparks.name = 'rage-sparks'; group.add(sparks);
    this.scene.add(group);
    return { heroId, at, group, wave, pillar, ring, sparks };
  }
  private blast(burst: Burst, positions: ReadonlyMap<string, THREE.Object3D>, time: number, ppm: number): boolean {
    const figure = positions.get(burst.heroId), since = time - burst.at, p = since / RAGE.burst;
    // The scene is drawn a moment behind the newest snapshot, so a burst arrives a little ahead of the clock and waits for it.
    if (!figure || p >= 1 || p < -1) { disposeEffect(burst.group); burst.group.removeFromParent(); return false; }
    burst.group.visible = p >= 0;
    const height = this.heights.get(burst.heroId) ?? 2, t = Math.max(0, p);
    burst.group.position.copy(figure.position);
    // The shock wave: thrown out low across the ground in the first half second, bending what it passes.
    const reach = .3 + 2.6 * (1 - (1 - Math.min(1, t / .45)) ** 2.6), wave = t < .5 ? Math.min(1, t * 14) * (1 - t / .5) ** 1.5 : 0;
    burst.wave.mesh.scale.set(reach, reach * .3, reach); burst.wave.mesh.position.y = .2; burst.wave.mesh.visible = wave > .002;
    burst.wave.uniforms.uTime.value = time; burst.wave.uniforms.uRipple.value = wave * .7;
    burst.wave.uniforms.uIor.value = 1 + .12 * wave; burst.wave.uniforms.uThickness.value = .7 * wave;
    // The column: fire coils up round the hero to about its height and burns away.
    const rise = 1 - (1 - Math.min(1, t / .3)) ** 3, fade = t < .12 ? t / .12 : (1 - (t - .12) / .88) ** 1.6;
    burst.pillar.scale.set(.5 + t * .25, height * 1.15 * rise, .5 + t * .25); burst.pillar.rotation.y = time * 5;
    shadeEffect(burst.pillar, time, fade * .8, Math.min(1, t * 1.6), ppm);
    // A thin ring of heat on the ground behind the wave.
    burst.ring.scale.setScalar(reach * .9); burst.ring.material.uniforms.uOpacity.value = (1 - t) ** 4 * .45; burst.ring.material.uniforms.uRing.value = 1 - t;
    burst.sparks.scale.set(1.2, 1, 1.2); burst.sparks.position.y = height * .5;
    shadeEffect(burst.sparks, time, (1 - t) * .7, Math.min(1, t * 1.8), ppm);
    this.lights.push({ position: figure.position.clone().setY(figure.position.y + height * .6), color: new THREE.Color(RAGE.heat), output: .5 * Math.exp(-t * 5) + .08 * fade });
    return true;
  }

  clear() {
    for (const id of [...this.raging.keys()]) this.extinguish(id);
    for (const burst of this.bursts) { disposeEffect(burst.group); burst.group.removeFromParent(); }
    this.bursts = []; this.flares.clear(); this.lights.length = 0; this.lastTime = undefined;
  }
}
